/**
 * Command palette (Phase 4). Triggered by Cmd/Ctrl-K.
 *
 * Substring-match across a small set of destinations: top-level views,
 * any saved filters loaded from the API, and (when supplied) a list of
 * recent submissions. Picking an item navigates to its hash.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from './ui/Dialog';

export interface PaletteCommand {
  id: string;
  label: string;
  /** Optional secondary text shown after the label (e.g. status, decade). */
  hint?: string;
  /** Hash to navigate to when picked. */
  hash: string;
  /** Group heading. */
  group:
    | 'Views'
    | 'Saved filters'
    | 'Recent submissions'
    | 'Guide sections';
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Custom commands to merge with the built-in views (saved filters / recent). */
  commands?: PaletteCommand[];
  /** Navigate when an item is picked. Defaults to `window.location.hash = …`. */
  onNavigate?: (hash: string) => void;
}

const BASE_COMMANDS: PaletteCommand[] = [
  { id: 'view-dashboard', label: 'Dashboard', hash: '#/', group: 'Views' },
  { id: 'view-submissions', label: 'Submissions', hash: '#/submissions', group: 'Views' },
  { id: 'view-activity', label: 'Activity Log', hash: '#/activity', group: 'Views' },
  {
    id: 'view-pending',
    label: 'Pending submissions',
    hash: '#/submissions?status=pending_review',
    group: 'Views',
  },
  {
    id: 'view-pipeline-errors',
    label: 'Pipeline errors',
    hash: '#/submissions?pipeline=error',
    group: 'Views',
  },
  {
    id: 'view-guide',
    label: 'Moderator guide',
    hint: 'Search the in-tool guide',
    hash: '#/guide',
    group: 'Views',
  },
];

function matches(command: PaletteCommand, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    command.label.toLowerCase().includes(q) ||
    (command.hint || '').toLowerCase().includes(q) ||
    command.id.toLowerCase().includes(q)
  );
}

export default function CommandPalette({
  open,
  onOpenChange,
  commands = [],
  onNavigate,
}: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const allCommands = useMemo(
    () => [...BASE_COMMANDS, ...commands],
    [commands],
  );

  const filtered = useMemo(
    () => allCommands.filter((c) => matches(c, query)),
    [allCommands, query],
  );

  // Reset state when reopened.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // Defer to let Radix mount the input.
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Keep activeIdx in bounds when the filter changes.
  useEffect(() => {
    if (activeIdx >= filtered.length) setActiveIdx(0);
  }, [filtered, activeIdx]);

  const groups = useMemo(() => {
    const out: Record<string, PaletteCommand[]> = {};
    for (const c of filtered) {
      out[c.group] = out[c.group] || [];
      out[c.group].push(c);
    }
    return out;
  }, [filtered]);

  const navigate = (hash: string) => {
    onOpenChange(false);
    if (onNavigate) onNavigate(hash);
    else if (typeof window !== 'undefined') window.location.hash = hash;
  };

  const handleKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = filtered[activeIdx];
      if (pick) navigate(pick.hash);
    }
  };

  // Build the flat index for highlighting.
  let cursor = -1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dlg-content--palette">
        <DialogTitle>
          <span className="sr-only">Command palette</span>
        </DialogTitle>
        <div className="palette" data-testid="command-palette" onKeyDown={handleKey}>
          <input
            ref={inputRef}
            className="palette__input"
            type="text"
            placeholder="Jump to view, filter or submission..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search commands"
            data-testid="palette-input"
          />
          {filtered.length === 0 ? (
            <p className="palette__empty">No matches.</p>
          ) : (
            <div className="palette__results">
              {(
                [
                  'Views',
                  'Saved filters',
                  'Recent submissions',
                  'Guide sections',
                ] as const
              ).map((g) => {
                const items = groups[g];
                if (!items || items.length === 0) return null;
                return (
                  <section key={g} className="palette__group" aria-label={g}>
                    <header className="palette__group-label">{g}</header>
                    <ul>
                      {items.map((c) => {
                        cursor += 1;
                        const isActive = cursor === activeIdx;
                        return (
                          <li key={c.id}>
                            <button
                              type="button"
                              className={`palette__item${
                                isActive ? ' palette__item--active' : ''
                              }`}
                              onClick={() => navigate(c.hash)}
                              onMouseEnter={() => setActiveIdx(cursor)}
                              aria-current={isActive ? 'true' : undefined}
                              data-testid={`palette-item-${c.id}`}
                            >
                              <span className="palette__label">{c.label}</span>
                              {c.hint && (
                                <span className="palette__hint">{c.hint}</span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
